import React from 'react';
import Button from '../../../components/form-control/Button';
import { superApprovePaymentEdit, superCancelPaymentApproval } from '../../../api/requests';
import { EyeIcon } from '@heroicons/react/24/solid';
import { PAYMENT_STATUS_STATES } from '../../../resources/enums';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '../../../utils/miscFunctions';

interface ApprovePaymentModalProps {
  data: any;
  title: string;
  onSubmitted: () => void;
  onCancel: () => void;
}

const ApprovePaymentModal = ({ data, onSubmitted, onCancel, title }: ApprovePaymentModalProps) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const queryClient = useQueryClient();
  const isInApproval = data?.paymentStatus === PAYMENT_STATUS_STATES.APPROVAL;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      if (isInApproval) {
        const response = await superApprovePaymentEdit(data?.id);
        if (response) {
          showToast.success(response?.message ?? 'Payment schedule edit approved successfully.');
          onSubmitted();
        }
      } else {
        const response = await superCancelPaymentApproval(data?.id);
        if (response) {
          showToast.success(response?.message ?? 'Payment approval cancelled successfully.');
          onSubmitted();
        }
      }
    } catch (error: any) {
      showToast.error( isInApproval ?
        error?.description ?? 'Failed to approve payment edit. Please try again later.'
        : error?.description ?? 'Failed to revert payment approval. Please try again later.'
      );
    } finally {
      setSubmitting(false);
      queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] })
    }
  };

  const handlePaymentEvidencePreview = () => {
    if (data?.evidenceUrl) {
      window.open(data.evidenceUrl, '_blank');
    }
  }

  return (
    <div className="bg-white rounded-xl dark:bg-darkBg">

      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="dark:text-darkBg-mainText">Evidence:</span>
          {data?.evidenceUrl &&
            // <ToolTip message="Preview Payment Evidence" direction="left">
              <EyeIcon onClick={handlePaymentEvidencePreview} className='h-4 w-4 cursor-pointer' />
            // </ToolTip>
          }
        </div>

        <div className="mt-4">
          {data?.evidenceUrl?.toLowerCase().endsWith('.pdf') ? (
            <div className="h-[50vh] w-full overflow-y-auto">
              <iframe 
                src={`${data.evidenceUrl}#toolbar=0`}
                className="w-full h-full rounded border border-gray-200 dark:border-darkBg-border"
                title="Payment Evidence PDF"
              />
            </div>
          ) : (
            <img 
              src={data?.evidenceUrl} 
              alt="evidence image" 
              className="h-auto max-h-[80vh] w-full rounded object-contain" 
              onError={(e) => {
                // Fallback to opening in new tab if image fails to load
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.style.display = 'none';
                const link = document.createElement('a');
                link.href = data?.evidenceUrl || '';
                link.target = '_blank';
                link.click();
              }}
            />
          )}
        </div>
      </div>

      <div className="p-5">
        <span className="dark:text-darkBg-mainText">Description:</span> <br />

        <p className='mt-2 dark:text-darkBg-mainText'>{data?.narration}</p>
      </div>

      <div className="flex justify-end space-x-6 mt-10 p-5">
        <Button
          label="Close"
          onClick={onCancel}
          bgColor="secondary"
          fullWidth
        />

        <Button
          label={isInApproval ? "Approve Payment" : "Revert Approval"}
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
          bgColor="primary"
          fullWidth
        />
      </div>
    </div>
  );
};

export default ApprovePaymentModal;
